// =====================================================
// BUDGET DETAIL PAGE
// Vista de solo lectura para presupuestos no en BORRADOR.
// Incluye:
//   - Cabecera con badge de estado y barra de acciones
//   - Datos del paciente y presupuesto
//   - Tabla de líneas agrupadas por sociedad
//   - Totales con descuentos
//   - Notas internas y públicas
//   - Modal de envío (email / imprimir)
//   - Modal de aceptación
//   - Modal de rechazo (con tipos de rechazo del catálogo)
//   - Modal de rectificación
//   - Timeline de versiones
// =====================================================
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Send,
  Check,
  X,
  RefreshCw,
  Download,
  FileText,
  User,
  Clock,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  History,
  Printer,
  Mail,
  Bell,
  Paperclip,
  CreditCard,
  Banknote,
  Edit2,
  FolderOpen,
  Trash2,
  Plus,
} from 'lucide-react';
import { budgetsApi, paymentsApi } from '../../api/budgets';
import { remindersApi } from '../../api/reminders';
import { budgetDocumentsApi, type BudgetDocument } from '../../api/budget-documents';
import { documentTypesApi, type DocumentTypeForSite } from '../../api/document-types';
import { rejectionTypesApi } from '../../api/rejection-types';
import { PdfViewer } from '../../components/budgets/PdfViewer';
import { ReminderModal } from '../../components/ui/ReminderModal';
import { useAuth } from '../../contexts/AuthContext';
import { BudgetStatusBadge } from '../../components/budgets/BudgetStatusBadge';
import { formatDate, formatDateTime, formatCurrency } from '../../utils/format';
import { toast } from '../../components/ui/Toast';
import type {
  BudgetResponse,
  BudgetListItem,
  BudgetPayment,
  CreatePaymentRequest,
  UpdatePaymentRequest,
  PaymentMethod,
} from '../../types/budget';

// ── Tipos internos ────────────────────────────────────────────────────────────
interface RejectionOption {
  id: string;
  name: string;
  requiresNote: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function groupLinesBySociety(lines: BudgetResponse['lines']) {
  const map = new Map<string, { societyId: string | null; societyName: string | null; lines: typeof lines; subtotal: number }>();
  for (const line of lines) {
    const key = line.society?.id || '__none__';
    if (!map.has(key)) {
      map.set(key, {
        societyId: line.society?.id || null,
        societyName: line.society?.name || null,
        lines: [],
        subtotal: 0,
      });
    }
    const g = map.get(key)!;
    g.lines.push(line);
    g.subtotal += line.total;
  }
  const result: Array<{ societyId: string | null; societyName: string | null; lines: typeof lines; subtotal: number }> = [];
  for (const [k, v] of map.entries()) {
    if (k !== '__none__') result.push(v);
  }
  if (map.has('__none__')) result.push(map.get('__none__')!);
  return result;
}

// ── Componente principal ──────────────────────────────────────────────────────
export function BudgetDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [budget, setBudget] = useState<BudgetResponse | null>(null);
  const [versions, setVersions] = useState<BudgetListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isActing, setIsActing] = useState(false);

  // ── Pagos ──────────────────────────────────────────────────────────────────
  const [payments, setPayments] = useState<BudgetPayment[]>([]);
  const [showPayments, setShowPayments] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState<BudgetPayment | null>(null);
  const [paymentForm, setPaymentForm] = useState<{ isFullPayment: boolean; isRefund: boolean; amount: string; method: PaymentMethod; notes: string; reason: string }>({
    isFullPayment: true, isRefund: false, amount: '', method: 'EFECTIVO', notes: '', reason: '',
  });
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<BudgetPayment | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  // ── Documentos ────────────────────────────────────────────────────────────
  const [documents, setDocuments] = useState<BudgetDocument[]>([]);
  const [showDocuments, setShowDocuments] = useState(false);
  const [showBudgetReminders, setShowBudgetReminders] = useState(false);
  const [budgetReminders, setBudgetReminders] = useState<any[]>([]);
  const [loadingBudgetReminders, setLoadingBudgetReminders] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [docTypes, setDocTypes] = useState<DocumentTypeForSite[]>([]);
  const [docForm, setDocForm] = useState<{ documentTypeId: string; customTitle: string; file: File | null; error: string | null }>({ documentTypeId: '', customTitle: '', file: null, error: null });
  const [isSavingDoc, setIsSavingDoc] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [docViewUrls, setDocViewUrls] = useState<Record<string, string | null>>({});

  const [auditLog, setAuditLog] = useState<any[]>([]);
  const [showAudit, setShowAudit] = useState(false);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [expandedActs, setExpandedActs] = useState<Set<number>>(new Set());
  const { user } = useAuth();
  const canSeeAudit = user?.role === 'GESTOR_SEDE' || user?.role === 'ADMIN';

  // Timeline visible
  const [showVersions, setShowVersions] = useState(false);

  // Modal: enviar
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');

  // Modal: aceptar
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [acceptNotes, setAcceptNotes] = useState('');
  const [signedDocument, setSignedDocument] = useState<File | null>(null);
  const [signedDocumentError, setSignedDocumentError] = useState<string | null>(null);

  // Modal: rechazar
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionOptions, setRejectionOptions] = useState<RejectionOption[]>([]);
  const [selectedRejection, setSelectedRejection] = useState('');
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [loadingRejections, setLoadingRejections] = useState(false);

  // Modal: rectificar
  const [showRectifyModal, setShowRectifyModal] = useState(false);
  const [rectifyReason, setRectifyReason] = useState('');

  // ── Carga ──────────────────────────────────────────────────────────────────
  const loadAuditLog = async () => {
    if (!id || loadingAudit) return;
    setLoadingAudit(true);
    try {
      const logs = await budgetsApi.getAuditLog(id);
      setAuditLog(logs);
    } catch {
      // Silencioso — auditoría no crítica
    } finally {
      setLoadingAudit(false);
    }
  };

  const loadBudget = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await budgetsApi.getById(id);

      // Si es BORRADOR redirigir al editor
      if (data.status === 'BORRADOR') {
        navigate(`/presupuestos/${id}/editar`, { replace: true });
        return;
      }

      setBudget(data);
      // Cargar recordatorios vinculados
      loadBudgetRemindersForBudget(data.id);
      setEmailTo(data.patient.email || '');

      // Cargar versiones si hay
      if (data.version > 1 || data.childBudgetsCount > 0) {
        const v = await budgetsApi.getVersionHistory(id);
        setVersions(v);
      }
    } catch (err: any) {
      setError(err.message || 'Error al cargar el presupuesto');
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadBudget();
  }, [loadBudget]);

  // ── Cargar tipos de rechazo del centro cuando se abre el modal ──────────
  useEffect(() => {
    if (!showRejectModal || rejectionOptions.length > 0) return;
    const load = async () => {
      setLoadingRejections(true);
      try {
        // Usa los tipos de rechazo asignados al centro activo del usuario
        const data = await rejectionTypesApi.getForSite();
        setRejectionOptions(data);
      } catch {
        // Si falla dejamos la lista vacía — el usuario puede escribir notas
      } finally {
        setLoadingRejections(false);
      }
    };
    load();
  }, [showRejectModal, rejectionOptions.length]);

  // ── Acciones ───────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!id) return;
    setIsActing(true);
    try {
      await budgetsApi.send(id, {
        sendEmail,
        email: sendEmail && emailTo ? emailTo : undefined,
        printOnly: !sendEmail,
      });
      toast.success('Enviado', sendEmail ? 'Presupuesto enviado por email' : 'Presupuesto marcado como enviado');
      setShowSendModal(false);
      loadBudget();
    } catch (err: any) {
      toast.error('Error', err.message);
    } finally {
      setIsActing(false);
    }
  };

  const handleAccept = async () => {
    if (!id) return;
    if (!signedDocument) {
      setSignedDocumentError('El presupuesto firmado (PDF) es obligatorio');
      return;
    }
    setSignedDocumentError(null);
    setIsActing(true);
    try {
      await budgetsApi.accept(id, { notes: acceptNotes || undefined }, signedDocument);
      toast.success('Aceptado', 'El presupuesto ha sido aceptado');
      setShowAcceptModal(false);
      setSignedDocument(null);
      loadBudget();
    } catch (err: any) {
      toast.error('Error', err.message);
    } finally {
      setIsActing(false);
    }
  };

  const handleReject = async () => {
    if (!id || !selectedRejection) {
      toast.error('Error', 'Selecciona un motivo de rechazo');
      return;
    }
    const option = rejectionOptions.find((o) => o.id === selectedRejection);
    if (option?.requiresNote && !rejectionNotes.trim()) {
      toast.error('Error', 'Esta opción requiere una nota explicativa');
      return;
    }
    setIsActing(true);
    try {
      await budgetsApi.reject(id, {
        rejectionOptionId: selectedRejection,
        rejectionNotes: rejectionNotes || undefined,
      });
      toast.success('Rechazado', 'El presupuesto ha sido rechazado');
      setShowRejectModal(false);
      loadBudget();
    } catch (err: any) {
      toast.error('Error', err.message);
    } finally {
      setIsActing(false);
    }
  };

  const handleRectify = async () => {
    if (!id) return;
    setIsActing(true);
    try {
      const newBudget = await budgetsApi.createRectification(id, {
        reason: rectifyReason || undefined,
      });
      toast.success('Rectificación creada', `Nuevo borrador: ${newBudget.budgetNumber}`);
      setShowRectifyModal(false);
      navigate(`/presupuestos/${newBudget.id}/editar`);
    } catch (err: any) {
      toast.error('Error', err.message);
    } finally {
      setIsActing(false);
    }
  };

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showReminderModal, setShowReminderModal] = useState(false);

  const loadBudgetRemindersForBudget = async (budgetId: string) => {
    if (!budgetId) return;
    setLoadingBudgetReminders(true);
    try {
      const data = await remindersApi.getAll({ budgetId: budgetId });
      setBudgetReminders(data);
    } catch { /* ignorar */ } finally { setLoadingBudgetReminders(false); }
  };

  const loadBudgetReminders = async () => {
    if (id) await loadBudgetRemindersForBudget(id);
  };

  const handleResendEmail = async () => {
    if (!resendEmail.trim() || !id) return;
    setIsSendingResend(true);
    try {
      await budgetsApi.resend(id, resendEmail.trim());
      toast.success('Email enviado', `Presupuesto enviado a ${resendEmail.trim()}`);
      setShowResendModal(false);
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo enviar el email');
    } finally { setIsSendingResend(false); }
  };
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [isSendingResend, setIsSendingResend] = useState(false);
  const [signedPdfUrl, setSignedPdfUrl] = useState<string | null>(null);

  // ── Funciones de pago ────────────────────────────────────────────────────
  const loadPayments = async () => {
    if (!id) return;
    setLoadingPayments(true);
    try {
      const data = await paymentsApi.getAll(id);
      setPayments(data);
    } catch { /* silencioso */ }
    finally { setLoadingPayments(false); }
  };

  const handleOpenPaymentModal = (payment?: BudgetPayment) => {
    setEditingPayment(payment || null);
    setPaymentError(null);
    if (payment) {
      setPaymentForm({
        isFullPayment: false,
        isRefund: payment.type === 'REFUND',
        amount: payment.amount.toString(),
        method: payment.method as PaymentMethod,
        notes: payment.notes || '',
        reason: '',
      });
    } else {
      const isDevolucion = (budget as any)?.paymentStatus === 'DEVOLUCION';
      setPaymentForm({ isFullPayment: true, isRefund: isDevolucion, amount: '', method: 'EFECTIVO', notes: '', reason: '' });
    }
    setShowPaymentModal(true);
  };

  const handleSavePayment = async () => {
    if (!id || !budget) return;
    setPaymentError(null);
    const pendingAmount = (budget as any).pendingAmount ?? 0;

    if (!paymentForm.isFullPayment) {
      const amt = parseFloat(paymentForm.amount);
      if (isNaN(amt) || amt <= 0) {
        setPaymentError(paymentForm.isRefund ? 'El importe de la devolución debe ser mayor que 0' : 'El importe del pago debe ser mayor que 0');
        return;
      }
      if (!editingPayment) {
        // En devoluciones pendingAmount es negativo → comparar contra su valor absoluto
        const limite = paymentForm.isRefund ? Math.abs(pendingAmount) : pendingAmount;
        if (amt > limite + 0.001) {
          setPaymentError(
            paymentForm.isRefund
              ? `El importe a devolver (${amt}€) no puede superar el saldo a favor (${Math.abs(pendingAmount).toFixed(2)}€)`
              : `El importe (${amt}€) no puede superar la deuda pendiente (${pendingAmount.toFixed(2)}€)`
          );
          return;
        }
      }
    }

    setIsSavingPayment(true);
    try {
      if (editingPayment) {
        const data: UpdatePaymentRequest = {
          method: paymentForm.method,
          notes: paymentForm.notes || undefined,
          reason: paymentForm.reason || undefined,
        };
        if (!paymentForm.isFullPayment) data.amount = parseFloat(paymentForm.amount);
        await paymentsApi.update(id, editingPayment.id, data);
        toast.success('Pago actualizado', 'El pago ha sido modificado correctamente');
      } else {
        // Para devoluciones, pendingAmount es negativo → usar Math.abs
        const amount = paymentForm.isFullPayment
          ? Math.abs(pendingAmount)
          : parseFloat(paymentForm.amount);
        const data: CreatePaymentRequest = {
          amount,
          method: paymentForm.method,
          notes: paymentForm.notes || undefined,
          type: paymentForm.isRefund ? 'REFUND' : 'PAYMENT',
        };
        await paymentsApi.create(id, data);
        toast.success('Pago registrado', `Se han registrado ${amount.toFixed(2)}€`);
      }
      setShowPaymentModal(false);
      await loadBudget();
      await loadPayments();
    } catch (err: any) {
      setPaymentError(err.message || 'Error al guardar el pago');
    } finally {
      setIsSavingPayment(false);
    }
  };

  const handleCancelPayment = async () => {
    if (!id || !cancelTarget) return;
    setIsCancelling(true);
    try {
      await paymentsApi.cancel(id, cancelTarget.id, paymentForm.reason || undefined);
      toast.success('Pago anulado', 'El pago ha sido anulado');
      setCancelTarget(null);
      await loadBudget();
      await loadPayments();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo anular el pago');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDownloadSigned = async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem('imi_access_token') || '';
      const response = await fetch('/api/v1/budgets/' + id + '/signed-document', {
        headers: { Authorization: 'Bearer ' + token },
      });
      if (!response.ok) throw new Error('No se pudo obtener el documento firmado');
      const blob = await response.blob();
      // Revocar URL anterior si existía
      if (signedPdfUrl) URL.revokeObjectURL(signedPdfUrl);
      const url = URL.createObjectURL(blob);
      setSignedPdfUrl(url);
    } catch (err: any) {
      toast.error('Error', 'No se pudo cargar el documento firmado');
    }
  };

  const handleDownloadPdf = async () => {
    if (!id) return;
    try {
      const blob = await budgetsApi.downloadPdf(id);
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err: any) {
      toast.error('Error', 'No se pudo cargar el PDF');
    }
  };

  const handleDownloadPdfCobros = async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem('imi_access_token') || '';
      const response = await fetch('/api/v1/budgets/' + id + '/pdf?cobros=1', {
        headers: { Authorization: 'Bearer ' + token },
      });
      if (!response.ok) throw new Error('No se pudo generar el PDF con cobros');
      const blob = await response.blob();
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err: any) {
      toast.error('Error', 'No se pudo cargar el PDF con cobros');
    }
  };

  // ── Funciones documentos ──────────────────────────────────────────────────
  const loadDocuments = async () => {
    if (!id) return;
    setLoadingDocuments(true);
    try { const data = await budgetDocumentsApi.getAll(id); setDocuments(data); }
    catch { /* silencioso */ }
    finally { setLoadingDocuments(false); }
  };

  const handleOpenDocModal = async () => {
    setDocForm({ documentTypeId: '', customTitle: '', file: null, error: null });
    try {
      const types = await documentTypesApi.getForSite();
      setDocTypes(types);
      if (types.length === 1) setDocForm(prev => ({ ...prev, documentTypeId: types[0].id }));
    } catch { setDocTypes([]); }
    setShowDocModal(true);
  };

  const handleUploadDoc = async () => {
    if (!id) return;
    if (!docForm.documentTypeId) { setDocForm(p => ({ ...p, error: 'Selecciona un tipo de documento' })); return; }
    if (!docForm.file) { setDocForm(p => ({ ...p, error: 'Adjunta un fichero PDF' })); return; }
    const docType = docTypes.find(t => t.id === docForm.documentTypeId);
    if (docType?.requiresCustomTitle && !docForm.customTitle.trim()) {
      setDocForm(p => ({ ...p, error: 'El título es obligatorio para este tipo de documento' })); return;
    }
    setIsSavingDoc(true);
    try {
      await budgetDocumentsApi.upload(id, docForm.documentTypeId, docForm.file, docForm.customTitle || undefined);
      toast.success('Documento adjuntado', docForm.file.name);
      setShowDocModal(false);
      await loadDocuments();
    } catch (err: any) {
      setDocForm(p => ({ ...p, error: err.message || 'Error al subir el documento' }));
    } finally { setIsSavingDoc(false); }
  };

  const handleDeleteDoc = async (docId: string) => {
    if (!id) return;
    setDeletingDocId(docId);
    try {
      await budgetDocumentsApi.delete(id, docId);
      toast.success('Eliminado', 'El documento ha sido eliminado');
      setDocViewUrls(prev => { const n = { ...prev }; delete n[docId]; return n; });
      await loadDocuments();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo eliminar el documento');
    } finally { setDeletingDocId(null); }
  };

  const handleViewDoc = async (doc: BudgetDocument) => {
    if (docViewUrls[doc.id]) {
      setDocViewUrls(prev => { const n = { ...prev }; delete n[doc.id]; return n; });
      return;
    }
    try {
      const token = localStorage.getItem('imi_access_token') || '';
      const res = await fetch(budgetDocumentsApi.getViewUrl(id!, doc.id), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('No se pudo cargar el documento');
      const blob = await res.blob();
      setDocViewUrls(prev => ({ ...prev, [doc.id]: URL.createObjectURL(blob) }));
    } catch (err: any) { toast.error('Error', err.message); }
  };

  // ── Loading / Error ────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/presupuestos')} className="p-2 rounded-lg hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div className="animate-pulse h-8 w-48 bg-gray-200 rounded" />
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-24 bg-white rounded-xl border border-gray-200" />
          <div className="h-64 bg-white rounded-xl border border-gray-200" />
        </div>
      </div>
    );
  }

  if (error || !budget) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/presupuestos')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" /> Volver a presupuestos
        </button>
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span>{error || 'Presupuesto no encontrado'}</span>
        </div>
      </div>
    );
  }

  const groups = groupLinesBySociety(budget.lines);
  const isEnviado = budget.status === 'ENVIADO';
  const isAceptado = budget.status === 'ACEPTADO';
  const isRechazado = budget.status === 'RECHAZADO';

  const requiresNote = rejectionOptions.find((o) => o.id === selectedRejection)?.requiresNote ?? false;

  return (
    <div className="space-y-4">

      {/* ── Cabecera ────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/presupuestos')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-gray-900 font-mono">
                {budget.budgetNumber}
              </h1>
              {budget.version > 1 && (
                <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full font-medium">
                  v{budget.version}
                </span>
              )}
              <BudgetStatusBadge status={budget.status} />
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              Creado el {formatDate(budget.createdAt)} por {budget.createdByName}
            </p>
          </div>
        </div>

        {/* Barra de acciones según estado */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* PDF con cobros — solo ACEPTADO (incluye estado de cobro) */}
          {isAceptado && (
            <button
              onClick={() => handleDownloadPdfCobros()}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-[#008DD0] rounded-lg text-[#008DD0] bg-sky-50 hover:bg-sky-100 transition-colors"
              title="Generar el PDF incluyendo el estado de cobro (pagado y pendiente)"
            >
              <FileText className="h-4 w-4" />
              PDF con cobros
            </button>
          )}
          {/* Ver PDF — siempre disponible */}
          <button
            onClick={() => pdfUrl ? (URL.revokeObjectURL(pdfUrl), setPdfUrl(null)) : handleDownloadPdf()}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <FileText className="h-4 w-4" />
            {pdfUrl ? 'Cerrar PDF' : 'PDF'}
          </button>
          {/* Recordatorio */}
          <button
            onClick={() => setShowReminderModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Bell className="h-4 w-4" />
            Recordatorio
          </button>

          {/* PDF firmado — solo si está aceptado y tiene documento */}
          {budget.signedDocumentUrl && (
            <button
              onClick={() => signedPdfUrl ? setSignedPdfUrl(null) : handleDownloadSigned()}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-green-200 rounded-lg text-green-700 bg-green-50 hover:bg-green-100 transition-colors"
              title="Ver presupuesto firmado por el paciente"
            >
              <FileText className="h-4 w-4" />
              {signedPdfUrl ? 'Cerrar firmado' : 'Firmado'}
            </button>
          )}

          {/* Versiones */}
          {(budget.version > 1 || budget.childBudgetsCount > 0 || versions.length > 0) && (
            <button
              onClick={() => setShowVersions(!showVersions)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <History className="h-4 w-4" />
              Versiones
              {showVersions ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          )}

          {/* ENVIADO: botón enviar (solo en estado ENVIADO se puede aceptar/rechazar) */}
          {isEnviado && (
            <>
              <button
                onClick={() => setShowAcceptModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Check className="h-4 w-4" />
                Aceptar
              </button>
              <button
                onClick={() => setShowRejectModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <X className="h-4 w-4" />
                Rechazar
              </button>
              <button
                onClick={() => setShowRectifyModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Rectificar
              </button>
            </>
          )}

          {/* ACEPTADO o RECHAZADO: solo rectificar */}
          {(isAceptado || isRechazado) && (
            <button
              onClick={() => setShowRectifyModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Rectificar
            </button>
          )}

          {/* Registrar pago — solo ACEPTADO con deuda pendiente */}
          {isAceptado && (budget as any).pendingAmount > 0 && (budget as any).paymentStatus !== 'DEVOLUCION' && (
            <button
              onClick={() => handleOpenPaymentModal()}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-[#008DD0] text-white rounded-lg hover:bg-[#0079b3] transition-colors"
            >
              <CreditCard className="h-4 w-4" />
              Registrar pago
            </button>
          )}
          {/* Registrar devolución — solo cuando paymentStatus es DEVOLUCION */}
          {isAceptado && (budget as any).paymentStatus === 'DEVOLUCION' && (
            <button
              onClick={() => handleOpenPaymentModal()}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <CreditCard className="h-4 w-4" />
              Registrar devolución
            </button>
          )}
        </div>
      </div>

      {/* ── Timeline de versiones ──────────────────────────────────────── */}
      {showVersions && versions.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <History className="h-4 w-4 text-gray-400" />
            Historial de versiones
          </h3>
          <div className="space-y-2">
            {versions.map((v) => (
              <button
                key={v.id}
                onClick={() => navigate(v.status === 'BORRADOR' ? `/presupuestos/${v.id}/editar` : `/presupuestos/${v.id}`)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors text-left ${
                  v.id === id
                    ? 'border-blue-200 bg-blue-50'
                    : 'border-gray-100 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm font-semibold text-gray-700">{v.budgetNumber}</span>
                  <BudgetStatusBadge status={v.status} size="sm" />
                </div>
                <div className="text-xs text-gray-400">{formatDate(v.createdAt)}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Sección de pagos (colapsable) ───────────────────────────── */}
      {(isAceptado || budget.paymentStatus === 'DEVOLUCION') && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() => { setShowPayments(!showPayments); if (!showPayments && payments.length === 0) loadPayments(); }}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-[#008DD0]/10 border border-[#008DD0]/20 flex items-center justify-center">
                <CreditCard className="h-3.5 w-3.5 text-[#008DD0]" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-800">Pagos y deuda</p>
                <p className="text-xs text-gray-400">
                  Pagado: {formatCurrency((budget as any).totalPaid ?? 0)} · Pendiente: {formatCurrency((budget as any).pendingAmount ?? 0)}
                </p>
              </div>
            </div>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showPayments ? 'rotate-180' : ''}`} />
          </button>

          {showPayments && (
            <div className="border-t border-gray-100">
              {loadingPayments ? (
                <div className="flex items-center justify-center py-8 gap-2 text-gray-400 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Cargando pagos...
                </div>
              ) : payments.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">
                  No hay pagos registrados aún
                </div>
              ) : (
                <div className="px-5 py-4">
                  {/* Timeline de pagos */}
                  <div className="relative">
                    <div className="absolute left-3 top-3 bottom-3 w-px bg-gray-100" />
                    <div className="space-y-0">
                      {payments.map((payment) => {
                        const isRefund   = payment.type === 'REFUND';
                        const isCancelled = payment.status === 'CANCELLED';
                        const isTransfer  = payment.isTransfer;

                        // Dot
                        const dotBg = isCancelled ? 'bg-red-100'
                          : isTransfer              ? 'bg-purple-100'
                          : isRefund                ? 'bg-blue-100'
                          :                           'bg-green-100';
                        const dotColor = isCancelled ? 'text-red-500'
                          : isTransfer              ? 'text-purple-500'
                          : isRefund                ? 'text-blue-500'
                          :                           'text-green-600';

                        // Chip
                        const chipStyle = isCancelled ? 'bg-red-50 text-red-600'
                          : isTransfer              ? 'bg-purple-50 text-purple-600'
                          : isRefund                ? 'bg-blue-50 text-blue-600'
                          :                           'bg-green-50 text-green-700';
                        const chipLabel = isCancelled ? 'ANULADO'
                          : isTransfer              ? 'TRASLADO'
                          : isRefund                ? 'DEVOLUCIÓN'
                          :                           'PAGO';

                        // Importe
                        const amountStyle = isCancelled ? 'line-through text-gray-400'
                          : isRefund ? 'text-blue-600 font-bold'
                          :            'text-gray-900 font-bold';

                        // Narrativa principal
                        const isTransferredRefund = isTransfer && payment.type === 'REFUND';
                        const narrative = isTransfer
                          ? isTransferredRefund
                            ? `Devolución trasladada desde ${payment.fromBudgetNumber || 'versión anterior'}`
                            : `Pago trasladado desde ${payment.fromBudgetNumber || 'versión anterior'}`
                          : isRefund
                          ? `Devolución registrada por ${payment.createdByName || 'Sistema'}`
                          : `Registrado por ${payment.createdByName || 'Sistema'}`;

                        return (
                          <div key={payment.id} className={`relative flex gap-3.5 pb-5 last:pb-0 ${isCancelled ? 'opacity-60' : ''}`}>
                            {/* Dot */}
                            <div className="relative z-10 shrink-0 mt-0.5">
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center ${dotBg}`} title={chipLabel}>
                                {isTransfer
                                  ? <CreditCard className={`h-3.5 w-3.5 ${dotColor}`} />
                                  : isRefund
                                  ? <RefreshCw  className={`h-3.5 w-3.5 ${dotColor}`} />
                                  : <Banknote   className={`h-3.5 w-3.5 ${dotColor}`} />
                                }
                              </div>
                            </div>

                            {/* Contenido */}
                            <div className="flex-1 min-w-0 pt-0.5">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  {/* Chip + importe + método */}
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${chipStyle}`}>
                                      {chipLabel}
                                    </span>
                                    <span className={`text-sm ${amountStyle}`}>
                                      {formatCurrency(payment.amount)}
                                    </span>
                                    <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                                      {payment.method}
                                    </span>
                                  </div>

                                  {/* Narrativa */}
                                  <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                                    {narrative}{' · '}{formatDateTime(payment.createdAt)}
                                  </p>

                                  {/* Notas del pago */}
                                  {payment.notes && (
                                    <p className="text-xs text-gray-400 italic mt-0.5">"{payment.notes}"</p>
                                  )}

                                  {/* Sub-logs: modificaciones y anulaciones */}
                                  {payment.logs.length > 1 && (
                                    <div className="mt-2 space-y-1.5 pl-3 border-l-2 border-gray-100">
                                      {payment.logs.slice(1).map(log => (
                                        <div key={log.id} className="text-xs text-gray-400">
                                          {log.action === 'MODIFIED' && (
                                            <span className="flex items-start gap-1">
                                              <Edit2 className="h-3 w-3 shrink-0 mt-0.5 text-amber-400" />
                                              <span>
                                                <span className="text-amber-600 font-medium">Modificado</span>
                                                {/* Importe — solo si cambió */}
                                                {log.previousAmount !== log.newAmount && log.previousAmount !== null && log.newAmount !== null && (
                                                  <span>
                                                    {': '}
                                                    <span className="line-through">{formatCurrency(log.previousAmount)}</span>
                                                    {' → '}
                                                    <span className="text-gray-600 font-medium">{formatCurrency(log.newAmount)}</span>
                                                  </span>
                                                )}
                                                {/* Método — solo si cambió */}
                                                {log.previousMethod && log.newMethod && log.previousMethod !== log.newMethod && (
                                                  <span className="ml-1">
                                                    {log.previousAmount !== log.newAmount ? ' ·' : ':'}{' '}
                                                    <span className="line-through text-gray-400">{log.previousMethod}</span>
                                                    {' → '}
                                                    <span className="text-gray-600 font-medium">{log.newMethod}</span>
                                                  </span>
                                                )}
                                                {/* Notas — solo si cambiaron */}
                                                {log.previousNotes !== log.newNotes && (log.previousNotes || log.newNotes) && (
                                                  <span className="ml-1">
                                                    {(log.previousAmount !== log.newAmount || (log.previousMethod && log.previousMethod !== log.newMethod)) ? ' ·' : ':'}{' '}
                                                    <span className="line-through text-gray-400 italic">{log.previousNotes || '—'}</span>
                                                    {' → '}
                                                    <span className="text-gray-600 italic">{log.newNotes || '—'}</span>
                                                  </span>
                                                )}
                                                {log.reason && <span className="italic"> · "{log.reason}"</span>}
                                                {log.createdByName && <span> · {log.createdByName}</span>}
                                                {' · '}{formatDateTime(log.createdAt)}
                                              </span>
                                            </span>
                                          )}
                                          {log.action === 'CANCELLED' && (
                                            <span className="flex items-start gap-1">
                                              <X className="h-3 w-3 shrink-0 mt-0.5 text-red-400" />
                                              <span>
                                                <span className="text-red-500 font-medium">Anulado</span>
                                                {log.reason    && <span className="italic"> · "{log.reason}"</span>}
                                                {log.createdByName && <span> · {log.createdByName}</span>}
                                                {' · '}{formatDateTime(log.createdAt)}
                                              </span>
                                            </span>
                                          )}
                                          {log.action === 'TRANSFERRED' && (
                                            <span className="flex items-start gap-1">
                                              <CreditCard className="h-3 w-3 shrink-0 mt-0.5 text-purple-400" />
                                              <span>
                                                <span className="text-purple-600 font-medium">
                                                  {payment.type === 'REFUND' ? 'Devolución trasladada' : 'Pago trasladado'}
                                                </span>
                                                {log.reason && <span className="italic"> · {log.reason}</span>}
                                                {log.createdByName && <span> · {log.createdByName}</span>}
                                                {' · '}{formatDateTime(log.createdAt)}
                                              </span>
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* Acciones */}
                                {payment.status === 'ACTIVE' && !payment.isTransfer && (
                                  <div className="flex items-center gap-1 shrink-0">
                                    <button
                                      onClick={() => handleOpenPaymentModal(payment)}
                                      className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                      title="Editar pago"
                                    >
                                      <Edit2 className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      onClick={() => { setCancelTarget(payment); setPaymentForm(prev => ({ ...prev, reason: '' })); }}
                                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Anular pago"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Sección de recordatorios del presupuesto ─────────────────────── */}
      {budget && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div
            onClick={() => { const opening = !showBudgetReminders; setShowBudgetReminders(opening); if (opening) loadBudgetReminders(); }}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-[#008DD0]/10 border border-[#008DD0]/20 flex items-center justify-center">
                <Bell className="h-3.5 w-3.5 text-[#008DD0]" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-800">Recordatorios</p>
                <p className="text-xs text-gray-400">
                  {budgetReminders.length > 0 ? `${budgetReminders.length} recordatorio${budgetReminders.length !== 1 ? 's' : ''}` : 'Sin recordatorios vinculados'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={e => { e.stopPropagation(); setShowReminderModal(true); }}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-[#008DD0]/10 text-[#008DD0] border border-[#008DD0]/20 rounded-lg hover:bg-[#008DD0]/20 transition-colors">
                <Plus className="h-3.5 w-3.5" /> Nuevo
              </button>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showBudgetReminders ? 'rotate-180' : ''}`} />
            </div>
          </div>
          {showBudgetReminders && (
            <div className="border-t border-gray-100">
              {loadingBudgetReminders ? (
                <div className="flex items-center justify-center py-8 gap-2 text-gray-400 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
                </div>
              ) : budgetReminders.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">No hay recordatorios vinculados a este presupuesto</div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {budgetReminders.map((reminder: any) => {
                    const sColors: Record<string,string> = { PENDIENTE: 'bg-amber-50 text-amber-700', ENVIADO: 'bg-blue-50 text-blue-700', FALLIDO: 'bg-red-50 text-red-700', CANCELADO: 'bg-gray-100 text-gray-500' };
                    const sLabels: Record<string,string> = { PENDIENTE: 'Pendiente', ENVIADO: 'Enviado', FALLIDO: 'Fallido', CANCELADO: 'Cancelado' };
                    return (
                      <div key={reminder.id} className="px-4 py-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-[#008DD0]/10 flex items-center justify-center shrink-0">
                            <Bell className="h-4 w-4 text-[#008DD0]" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800">{reminder.scheduledDate.split('-').reverse().join('/')}</p>
                            <p className="text-xs text-gray-400 truncate">{reminder.resolvedMessage}</p>
                            {reminder.failReasonLabel && <p className="text-xs text-red-500 mt-0.5">{reminder.failReasonLabel}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sColors[reminder.status] || 'bg-gray-100 text-gray-500'}`}>
                            {sLabels[reminder.status] || reminder.status}
                          </span>
                          {reminder.appointmentOutcome === 'CITED' && <span className="text-xs text-green-600">Con cita</span>}
                          {reminder.appointmentOutcome === 'NOT_CITED' && <span className="text-xs text-red-600">Sin cita</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Sección de documentos adjuntos ──────────────────────────────── */}
      {isAceptado && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div
            onClick={() => { setShowDocuments(prev => !prev); if (!showDocuments && documents.length === 0) loadDocuments(); }}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center">
                <FolderOpen className="h-3.5 w-3.5 text-amber-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-800">Documentación adjunta</p>
                <p className="text-xs text-gray-400">
                  {documents.length > 0 ? `${documents.length} documento${documents.length !== 1 ? 's' : ''} adjunto${documents.length !== 1 ? 's' : ''}` : 'Sin documentos adjuntos'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={e => { e.stopPropagation(); handleOpenDocModal(); }}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors">
                <Plus className="h-3.5 w-3.5" /> Adjuntar
              </button>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showDocuments ? 'rotate-180' : ''}`} />
            </div>
          </div>
          {showDocuments && (
            <div className="border-t border-gray-100">
              {loadingDocuments ? (
                <div className="flex items-center justify-center py-8 gap-2 text-gray-400 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Cargando documentos...
                </div>
              ) : documents.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">No hay documentos adjuntos</div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {documents.map(doc => (
                    <div key={doc.id} className="px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                            <FileText className="h-4 w-4 text-amber-600" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">{doc.documentTypeCode}</span>
                              <span className="text-sm font-medium text-gray-800 truncate">{doc.customTitle || doc.documentTypeName}</span>
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {doc.uploadedByName || 'Sistema'} · {formatDateTime(doc.createdAt)}
                              {doc.budgetNumber !== budget?.budgetNumber && (
                                <span className="ml-1.5 text-purple-500">· Subido en {doc.budgetNumber}</span>
                              )}
                              <span className="ml-1.5">· {(doc.fileSize / 1024).toFixed(0)} KB</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => handleViewDoc(doc)}
                            className={`p-1.5 rounded-lg transition-colors ${docViewUrls[doc.id] ? 'text-amber-600 bg-amber-50' : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'}`}
                            title={docViewUrls[doc.id] ? 'Cerrar visor' : 'Ver documento'}>
                            <FileText className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => handleDeleteDoc(doc.id)} disabled={deletingDocId === doc.id}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Eliminar documento">
                            {deletingDocId === doc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      </div>
                      {docViewUrls[doc.id] && (
                        <div className="mt-3 rounded-xl overflow-hidden border border-amber-100">
                          <PdfViewer url={docViewUrls[doc.id]!} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Visor PDF del presupuesto (inline) ───────────────────────── */}
      {pdfUrl && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-[#008DD0]/10 border border-[#008DD0]/20 flex items-center justify-center shrink-0">
                <FileText className="h-3.5 w-3.5 text-[#008DD0]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Presupuesto</p>
                <p className="text-xs text-gray-400">
                  {budget?.budgetNumber} · Versión actual
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {budget?.status === 'ENVIADO' && budget?.patient?.email && (
                <button
                  onClick={() => { setResendEmail(budget?.patient?.email || ''); setShowResendModal(true); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-lg transition-colors"
                >
                  <Mail className="h-3.5 w-3.5" />
                  Reenviar email
                </button>
              )}
              <a
                href={pdfUrl}
                download={'presupuesto-' + (budget?.budgetNumber || '') + '.pdf'}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                title="Descargar PDF"
              >
                <Download className="h-3.5 w-3.5" />
                Descargar
              </a>
              <button
                onClick={() => { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Cerrar visor"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <PdfViewer url={pdfUrl} />
        </div>
      )}

      {/* ── Visor PDF firmado (inline) ────────────────────────────────── */}
      {signedPdfUrl && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          {/* Cabecera */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-green-50 border border-green-100 flex items-center justify-center shrink-0">
                <FileText className="h-3.5 w-3.5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Documento firmado</p>
                <p className="text-xs text-gray-400">
                  Presupuesto {budget?.budgetNumber} · Firmado por el paciente
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Descargar */}
              <a
                href={signedPdfUrl}
                download={'presupuesto-firmado-' + (budget?.budgetNumber || '') + '.pdf'}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                title="Descargar PDF firmado"
              >
                <Download className="h-3.5 w-3.5" />
                Descargar
              </a>
              {/* Cerrar */}
              <button
                onClick={() => { URL.revokeObjectURL(signedPdfUrl); setSignedPdfUrl(null); }}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Cerrar visor"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Visor PDF.js — sin controles nativos del navegador */}
          <PdfViewer url={signedPdfUrl} />
        </div>
      )}

      {/* ── Datos del presupuesto ──────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Paciente */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <User className="h-3.5 w-3.5" /> Paciente
          </h3>
          <button
            type="button"
            onClick={() => navigate(`/pacientes/${budget.patient.id}`)}
            className="font-semibold text-gray-900 hover:text-[#008DD0] hover:underline transition-colors cursor-pointer text-left"
            title="Ver ficha del paciente"
          >{budget.patient.fullName}</button>
          <p className="text-sm text-gray-500 mt-0.5">
            {budget.patient.documentType}: {budget.patient.documentNumber}
          </p>
          {budget.patient.email && (
            <p className="text-sm text-gray-500">{budget.patient.email}</p>
          )}
          {budget.patient.phonePrimary && (
            <p className="text-sm text-gray-500">{budget.patient.phonePrimary}</p>
          )}
        </div>

        {/* Detalles del presupuesto */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" /> Presupuesto
          </h3>
          <div className="space-y-1.5 text-sm">
            {budget.treatment && (
              <div className="flex justify-between">
                <span className="text-gray-500">Tratamiento</span>
                <span className="font-medium text-gray-800">{budget.treatment.name}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">Centro</span>
              <span className="font-medium text-gray-800">{budget.siteName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Creado</span>
              <span className="text-gray-700">{formatDate(budget.createdAt)}</span>
            </div>
            {budget.sentAt && (
              <div className="flex justify-between">
                <span className="text-gray-500">Enviado</span>
                <span className="text-blue-600">{formatDate(budget.sentAt)}</span>
              </div>
            )}
            {budget.acceptedAt && (
              <div className="flex justify-between">
                <span className="text-gray-500">Aceptado</span>
                <span className="text-green-600">{formatDate(budget.acceptedAt)}</span>
              </div>
            )}
            {budget.rejectedAt && (
              <div className="flex justify-between">
                <span className="text-gray-500">Rechazado</span>
                <span className="text-red-600">{formatDate(budget.rejectedAt)}</span>
              </div>
            )}
          </div>

          {/* Info de rechazo */}
          {isRechazado && budget.rejectionOption && (
            <div className="mt-3 p-2.5 bg-red-50 border border-red-100 rounded-lg">
              <p className="text-xs font-medium text-red-700">{budget.rejectionOption.name}</p>
              {budget.rejectionNotes && (
                <p className="text-xs text-red-600 mt-0.5">{budget.rejectionNotes}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Líneas agrupadas por sociedad ──────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">
            Actos médicos ({budget.linesCount})
          </h3>
        </div>

        {groups.map((group, gi) => (
          <div key={gi} className="border-b border-gray-100 last:border-b-0">
            {/* Cabecera de grupo (si hay más de una sociedad) */}
            {groups.length > 1 && (
              <div className="px-4 py-2 bg-[#008DD0]/5 border-b border-[#008DD0]/10">
                <span className="text-xs font-semibold text-[#008DD0] uppercase tracking-wider">
                  {group.societyName || 'Sin sociedad'}
                </span>
              </div>
            )}

            {/* Tabla de líneas */}
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="px-4 py-2 text-left text-xs text-gray-400 font-medium">Acto</th>
                  <th className="px-4 py-2 text-center text-xs text-gray-400 font-medium hidden sm:table-cell">Piezas</th>
                  <th className="px-4 py-2 text-center text-xs text-gray-400 font-medium">Ud.</th>
                  <th className="px-4 py-2 text-right text-xs text-gray-400 font-medium hidden sm:table-cell">P. Unit.</th>
                  <th className="px-4 py-2 text-center text-xs text-gray-400 font-medium hidden md:table-cell">Dto.</th>
                  <th className="px-4 py-2 text-right text-xs text-gray-400 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {group.lines.map((line, li) => (
                  <tr key={li} className={li % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}>
                    <td className="px-4 py-2.5">
                      <span className="font-mono text-xs text-gray-400 mr-2">{line.medicalActCode}</span>
                      <span className="text-gray-800">{line.medicalActName}</span>
                      {line.toothNumbers && (
                        <span className="ml-2 text-xs text-gray-400">({line.toothNumbers})</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center text-gray-500 hidden sm:table-cell">
                      {line.toothNumbers || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-center text-gray-700 font-medium">
                      {line.quantity}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-600 hidden sm:table-cell">
                      {formatCurrency(line.unitPrice)}
                    </td>
                    <td className="px-4 py-2.5 text-center hidden md:table-cell">
                      {line.discountType ? (
                        <span className="text-green-600 text-xs font-medium">
                          {line.discountType === 'PERCENTAGE'
                            ? `${line.discountValue}%`
                            : `-${formatCurrency(line.discountValue || 0)}`}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-gray-900">
                      {formatCurrency(line.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {groups.length > 1 && (
                <tfoot>
                  <tr className="bg-[#008DD0]/5 border-t border-[#008DD0]/10">
                    <td colSpan={5} className="px-4 py-2 text-right text-xs font-semibold text-[#008DD0]">
                      Subtotal {group.societyName || 'Sin sociedad'}:
                    </td>
                    <td className="px-4 py-2 text-right text-sm font-bold text-[#008DD0]">
                      {formatCurrency(group.subtotal)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ))}
      </div>

      {/* ── Totales ────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col items-end gap-1.5 text-sm">
          <div className="flex items-center justify-between w-full max-w-xs">
            <span className="text-gray-500">Subtotal</span>
            <span className="font-medium text-gray-800">{formatCurrency(budget.subtotal)}</span>
          </div>
          {budget.discountAmount > 0 && (
            <div className="flex items-center justify-between w-full max-w-xs">
              <span className="text-gray-500">
                Descuento{budget.discountType === 'PERCENTAGE' ? ` (${budget.discountValue}%)` : ''}
              </span>
              <span className="text-green-600 font-medium">- {formatCurrency(budget.discountAmount)}</span>
            </div>
          )}
          <div className="flex items-center justify-between w-full max-w-xs pt-1.5 border-t border-gray-200">
            <span className="font-bold text-gray-900 text-base">TOTAL</span>
            <span className="font-bold text-[#008DD0] text-xl">{formatCurrency(budget.total)}</span>
          </div>
        </div>
      </div>

      {/* ── Notas ──────────────────────────────────────────────────────── */}
      {(budget.publicNotes || budget.internalNotes) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {budget.publicNotes && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Notas públicas</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{budget.publicNotes}</p>
            </div>
          )}
          {budget.internalNotes && (
            <div className="bg-white rounded-xl border border-amber-100 p-4">
              <h4 className="text-xs font-semibold text-amber-500 uppercase tracking-wider mb-2">Notas internas</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{budget.internalNotes}</p>
            </div>
          )}
        </div>
      )}


      {/* ── Historial de auditoría (solo GESTOR_SEDE y ADMIN) ──────────── */}
      {canSeeAudit && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() => {
              setShowAudit(!showAudit);
              if (!showAudit && auditLog.length === 0) loadAuditLog();
            }}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-400" />
              <span className="text-sm font-semibold text-gray-700">Historial de cambios</span>
              {auditLog.length > 0 && (
                <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                  {auditLog.length}
                </span>
              )}
            </div>
            {showAudit ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
          </button>

          {showAudit && (
            <div className="border-t border-gray-100">
              {loadingAudit ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
                </div>
              ) : auditLog.length === 0 ? (
                <p className="px-5 py-5 text-sm text-gray-400 italic">
                  No hay registros de actividad para este presupuesto.
                </p>
              ) : (
                <div className="px-5 py-4">
                  <div className="relative">
                    <div className="absolute left-3.5 top-4 bottom-4 w-px bg-gray-100" />
                    <div className="space-y-0">
                      {auditLog.map((log: any, i: number) => {
                        const msg = getAuditMessage(log);
                        return (
                          <div key={i} className="relative flex gap-4 pb-5 last:pb-0">
                            <div className={['relative z-10 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center', msg.dotBg].join(' ')}>
                              <AuditTimelineIcon action={log.action} colorClass={msg.iconColor} />
                            </div>
                            <div className="flex-1 min-w-0 pt-0.5">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  {log.budgetNumber && (
                                    <span className="inline-block mb-1 text-xs font-mono text-gray-400 bg-gray-50 border border-gray-100 rounded px-1.5 py-0.5">
                                      {log.budgetNumber}
                                    </span>
                                  )}
                                  <p className="text-sm font-medium text-gray-800 leading-snug">
                                    {msg.title}
                                  </p>
                                  {msg.subtitle && (
                                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                                      {msg.subtitle}
                                    </p>
                                  )}
                                  {log.details?.acts?.length > 0 && (
                                    <div className="mt-1.5">
                                      <button
                                        onClick={() => setExpandedActs(prev => {
                                          const next = new Set(prev);
                                          next.has(i) ? next.delete(i) : next.add(i);
                                          return next;
                                        })}
                                        className="inline-flex items-center gap-1 text-xs text-[#008DD0] hover:text-[#0079b3] transition-colors"
                                      >
                                        {expandedActs.has(i)
                                          ? <><ChevronUp className="h-3 w-3" /> Ocultar actos</>
                                          : <><ChevronDown className="h-3 w-3" /> Ver actos ({log.details.acts.length})</>
                                        }
                                      </button>
                                      {expandedActs.has(i) && (
                                        <ul className="mt-1.5 space-y-0.5 pl-1 border-l-2 border-gray-100 ml-0.5">
                                          {log.details.acts.map((act: string, ai: number) => (
                                            <li key={ai} className="text-xs text-gray-600 pl-2 py-0.5">
                                              {act}
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  )}
                                  {log.userName && (
                                    <p className="text-xs text-gray-400 mt-1">
                                      por {log.userName}
                                    </p>
                                  )}
                                </div>
                                <span className="text-xs text-gray-400 shrink-0 whitespace-nowrap mt-0.5">
                                  {formatDateTime(log.createdAt)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MODALES
      ══════════════════════════════════════════════════════════════════ */}

      {/* ── Modal: Adjuntar documento ──────────────────────────────────── */}
      {showDocModal && (
        <Modal title="Adjuntar documento" onClose={() => setShowDocModal(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de documento *</label>
              {docTypes.length === 0 ? (
                <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-xl">
                  No hay tipos de documento asignados a este centro. Configúralos en Catálogos Maestros → Tipos de Documento.
                </p>
              ) : (
                <select value={docForm.documentTypeId}
                  onChange={e => setDocForm(p => ({ ...p, documentTypeId: e.target.value, error: null }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#008DD0]/20 focus:border-[#008DD0]">
                  <option value="">Seleccionar tipo...</option>
                  {docTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              )}
            </div>
            {docTypes.find(t => t.id === docForm.documentTypeId)?.requiresCustomTitle && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                <input type="text" value={docForm.customTitle}
                  onChange={e => setDocForm(p => ({ ...p, customTitle: e.target.value, error: null }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                  placeholder="Ej: Consentimiento ortodoncia adultos" maxLength={200} />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fichero PDF *</label>
              {docForm.file ? (
                <div className="flex items-center justify-between p-2.5 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center gap-2 text-sm text-green-700 min-w-0">
                    <Paperclip className="h-4 w-4 shrink-0" />
                    <span className="truncate font-medium">{docForm.file.name}</span>
                    <span className="text-green-500 shrink-0">({(docForm.file.size / 1024).toFixed(0)} KB)</span>
                  </div>
                  <button onClick={() => setDocForm(p => ({ ...p, file: null }))} className="ml-2 p-1 text-gray-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 rounded-xl cursor-pointer transition-colors">
                  <Paperclip className="h-6 w-6 text-gray-400 mb-1" />
                  <span className="text-sm text-gray-500">Haz clic para adjuntar el PDF</span>
                  <span className="text-xs text-gray-400">Solo PDF · Máx. 10 MB</span>
                  <input type="file" accept="application/pdf" className="hidden" onChange={e => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.type !== 'application/pdf') { setDocForm(p => ({ ...p, error: 'Solo se permiten ficheros PDF' })); return; }
                    if (f.size > 10 * 1024 * 1024) { setDocForm(p => ({ ...p, error: 'El fichero supera los 10 MB' })); return; }
                    setDocForm(p => ({ ...p, file: f, error: null }));
                  }} />
                </label>
              )}
            </div>
            {docForm.error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                <AlertCircle className="h-4 w-4 shrink-0" /> {docForm.error}
              </div>
            )}
          </div>
          <ModalFooter
            onCancel={() => setShowDocModal(false)}
            onConfirm={handleUploadDoc}
            isLoading={isSavingDoc}
            confirmLabel="Adjuntar documento"
            confirmClass="bg-amber-600 hover:bg-amber-700 text-white"
          />
        </Modal>
      )}

      {/* ── Modal: Registrar / Editar pago ─────────────────────────────── */}
      {showPaymentModal && (
        <Modal
          title={editingPayment ? 'Editar pago' : 'Registrar pago'}
          onClose={() => { setShowPaymentModal(false); setPaymentError(null); }}
        >
          <div className="space-y-4">
            {/* Resumen deuda */}
            <div className="grid grid-cols-3 gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-0.5">Total presupuesto</p>
                <p className="text-sm font-bold text-gray-800">{formatCurrency(budget?.total ?? 0)}</p>
              </div>
              <div className="text-center border-x border-gray-200">
                <p className="text-xs text-gray-500 mb-0.5">Ya pagado</p>
                <p className="text-sm font-bold text-green-600">{formatCurrency((budget as any)?.totalPaid ?? 0)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-0.5">Pendiente</p>
                <p className="text-sm font-bold text-red-600">{formatCurrency((budget as any)?.pendingAmount ?? 0)}</p>
              </div>
            </div>

            {/* Tipo de pago/devolución — solo en creación */}
            {!editingPayment && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {(budget as any)?.paymentStatus === 'DEVOLUCION' ? 'Tipo de devolución' : 'Tipo de pago'}
                </label>
                <div className="space-y-2">
                  <label className={[
                    'flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:bg-gray-50 transition-colors',
                    paymentForm.isFullPayment ? 'border-[#008DD0] bg-blue-50/30' : 'border-gray-200',
                  ].join(' ')}>
                    <input type="radio" checked={paymentForm.isFullPayment}
                      onChange={() => setPaymentForm(p => ({ ...p, isFullPayment: true, amount: '' }))}
                      className="text-[#008DD0]" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {(budget as any)?.paymentStatus === 'DEVOLUCION' ? 'Devolución total' : 'Pago total'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {(budget as any)?.paymentStatus === 'DEVOLUCION'
                          ? `Devolver importe completo: ${formatCurrency(Math.abs((budget as any)?.pendingAmount ?? 0))}`
                          : `Salda la deuda completa: ${formatCurrency((budget as any)?.pendingAmount ?? 0)}`
                        }
                      </p>
                    </div>
                  </label>
                  <label className={[
                    'flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:bg-gray-50 transition-colors',
                    !paymentForm.isFullPayment ? 'border-[#008DD0] bg-blue-50/30' : 'border-gray-200',
                  ].join(' ')}>
                    <input type="radio" checked={!paymentForm.isFullPayment}
                      onChange={() => setPaymentForm(p => ({ ...p, isFullPayment: false }))}
                      className="text-[#008DD0]" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {(budget as any)?.paymentStatus === 'DEVOLUCION' ? 'Devolución parcial' : 'Pago parcial'}
                      </p>
                      <p className="text-xs text-gray-400">Indica el importe</p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Importe — pago parcial o edición */}
            {(!paymentForm.isFullPayment || editingPayment) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Importe {editingPayment ? '(modificar)' : '*'}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                  <input
                    type="number"
                    value={paymentForm.amount}
                    onChange={e => setPaymentForm(p => ({ ...p, amount: e.target.value }))}
                    className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#008DD0]/20 focus:border-[#008DD0]"
                    placeholder="0.00" min="0.01" step="0.01"
                  />
                </div>
              </div>
            )}

            {/* Método de pago */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Método de pago *</label>
              <select
                value={paymentForm.method}
                onChange={e => setPaymentForm(p => ({ ...p, method: e.target.value as PaymentMethod }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#008DD0]/20 focus:border-[#008DD0]"
              >
                <option value="EFECTIVO">Efectivo</option>
                <option value="TARJETA">Tarjeta</option>
                <option value="TRANSFERENCIA">Transferencia</option>
                <option value="FINANCIACION">Financiación</option>
              </select>
            </div>

            {/* Notas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas (opcional)</label>
              <input type="text" value={paymentForm.notes}
                onChange={e => setPaymentForm(p => ({ ...p, notes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                placeholder="Observaciones..." maxLength={200} />
            </div>

            {/* Motivo — solo edición */}
            {editingPayment && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo del cambio</label>
                <input type="text" value={paymentForm.reason}
                  onChange={e => setPaymentForm(p => ({ ...p, reason: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                  placeholder="Motivo de la modificación..." maxLength={200} />
              </div>
            )}

            {paymentError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                <AlertCircle className="h-4 w-4 shrink-0" /> {paymentError}
              </div>
            )}
          </div>
          <ModalFooter
            onCancel={() => { setShowPaymentModal(false); setPaymentError(null); }}
            onConfirm={handleSavePayment}
            isLoading={isSavingPayment}
            confirmLabel={editingPayment ? 'Guardar cambios' : 'Registrar pago'}
            confirmClass="bg-[#008DD0] hover:bg-[#0079b3] text-white"
          />
        </Modal>
      )}

      {/* ── Modal: Anular pago ──────────────────────────────────────────── */}
      {cancelTarget && (
        <Modal title="Anular pago" onClose={() => setCancelTarget(null)}>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Anular pago de {formatCurrency(cancelTarget.amount)}</p>
                <p className="text-xs text-red-600 mt-0.5">El pago quedará marcado como anulado. Esta acción queda registrada.</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Motivo de anulación (opcional)</label>
              <input type="text" value={paymentForm.reason}
                onChange={e => setPaymentForm(p => ({ ...p, reason: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                placeholder="Motivo..." maxLength={200} />
            </div>
          </div>
          <ModalFooter
            onCancel={() => setCancelTarget(null)}
            onConfirm={handleCancelPayment}
            isLoading={isCancelling}
            confirmLabel="Anular pago"
            confirmClass="bg-red-600 hover:bg-red-700 text-white"
          />
        </Modal>
      )}

      {/* ── Modal: Enviar ──────────────────────────────────────────────── */}
      {showSendModal && (
        <Modal title="Enviar presupuesto" onClose={() => setShowSendModal(false)}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              El presupuesto pasará a estado <strong>Enviado</strong> y quedará bloqueado para edición.
            </p>

            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="sendMode"
                  checked={!sendEmail}
                  onChange={() => setSendEmail(false)}
                  className="text-[#008DD0]"
                />
                <div>
                  <div className="flex items-center gap-2 font-medium text-sm text-gray-800">
                    <Printer className="h-4 w-4 text-gray-400" /> Solo marcar como enviado
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">El estado cambia pero no se envía email</p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="sendMode"
                  checked={sendEmail}
                  onChange={() => setSendEmail(true)}
                  className="text-[#008DD0]"
                />
                <div>
                  <div className="flex items-center gap-2 font-medium text-sm text-gray-800">
                    <Mail className="h-4 w-4 text-gray-400" /> Enviar PDF por email
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">Se genera el PDF y se adjunta al correo del paciente</p>
                </div>
              </label>
            </div>

            {sendEmail && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email del destinatario
                </label>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  placeholder="email@paciente.com"
                />
              </div>
            )}
          </div>

          <ModalFooter
            onCancel={() => setShowSendModal(false)}
            onConfirm={handleSend}
            isLoading={isActing}
            confirmLabel={sendEmail ? 'Enviar email' : 'Marcar como enviado'}
            confirmClass="bg-[#008DD0] hover:bg-[#0079b3] text-white"
          />
        </Modal>
      )}

      {/* ── Modal: Aceptar ─────────────────────────────────────────────── */}
      {showAcceptModal && (
        <Modal title="Aceptar presupuesto" onClose={() => { setShowAcceptModal(false); setSignedDocument(null); setSignedDocumentError(null); }}>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <Check className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              <p className="text-sm text-green-800">
                El presupuesto pasará a estado <strong>Aceptado</strong>.
                Es obligatorio adjuntar el presupuesto firmado por el paciente.
              </p>
            </div>

            {/* Upload presupuesto firmado — obligatorio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Presupuesto firmado (PDF) <span className="text-red-500">*</span>
              </label>
              {signedDocument ? (
                <div className="flex items-center justify-between p-2.5 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-green-700 min-w-0">
                    <Paperclip className="h-4 w-4 shrink-0" />
                    <span className="truncate font-medium">{signedDocument.name}</span>
                    <span className="text-green-500 shrink-0">({(signedDocument.size / 1024).toFixed(0)} KB)</span>
                  </div>
                  <button
                    onClick={() => { setSignedDocument(null); setSignedDocumentError(null); }}
                    className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className={[
                  'flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors',
                  signedDocumentError ? 'border-red-300 bg-red-50 hover:bg-red-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100',
                ].join(' ')}>
                  <div className="flex flex-col items-center gap-1">
                    <Paperclip className={['h-6 w-6', signedDocumentError ? 'text-red-400' : 'text-gray-400'].join(' ')} />
                    <span className="text-sm text-gray-500">Haz clic para adjuntar el PDF firmado</span>
                    <span className="text-xs text-gray-400">Solo PDF · Máx. 10 MB</span>
                  </div>
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        if (f.type !== 'application/pdf') {
                          setSignedDocumentError('Solo se permiten ficheros PDF');
                          return;
                        }
                        if (f.size > 10 * 1024 * 1024) {
                          setSignedDocumentError('El fichero supera los 10 MB');
                          return;
                        }
                        setSignedDocument(f);
                        setSignedDocumentError(null);
                      }
                    }}
                  />
                </label>
              )}
              {signedDocumentError && (
                <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" /> {signedDocumentError}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas (opcional)
              </label>
              <textarea
                value={acceptNotes}
                onChange={(e) => setAcceptNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none"
                placeholder="Observaciones sobre la aceptación..."
              />
            </div>
          </div>
          <ModalFooter
            onCancel={() => { setShowAcceptModal(false); setSignedDocument(null); setSignedDocumentError(null); }}
            onConfirm={handleAccept}
            isLoading={isActing}
            confirmLabel="Confirmar aceptación"
            confirmClass="bg-green-600 hover:bg-green-700 text-white"
          />
        </Modal>
      )}

      {/* ── Modal: Rechazar ────────────────────────────────────────────── */}
      {showRejectModal && (
        <Modal title="Rechazar presupuesto" onClose={() => setShowRejectModal(false)}>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <X className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                El presupuesto pasará a estado <strong>Rechazado</strong>.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motivo de rechazo *
              </label>
              {loadingRejections ? (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" /> Cargando motivos...
                </div>
              ) : rejectionOptions.length > 0 ? (
                <select
                  value={selectedRejection}
                  onChange={(e) => setSelectedRejection(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                >
                  <option value="">Seleccionar motivo...</option>
                  {rejectionOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
              ) : (
                <p className="text-xs text-gray-400 italic">
                  No hay motivos configurados. Escribe un motivo en las notas.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas{requiresNote ? ' *' : ' (opcional)'}
              </label>
              <textarea
                value={rejectionNotes}
                onChange={(e) => setRejectionNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none"
                placeholder="Motivo detallado del rechazo..."
              />
            </div>
          </div>
          <ModalFooter
            onCancel={() => setShowRejectModal(false)}
            onConfirm={handleReject}
            isLoading={isActing}
            confirmLabel="Confirmar rechazo"
            confirmClass="bg-red-600 hover:bg-red-700 text-white"
          />
        </Modal>
      )}

      {/* ── Modal: Rectificar ──────────────────────────────────────────── */}
      {showRectifyModal && (
        <Modal title="Crear rectificación" onClose={() => setShowRectifyModal(false)}>
          <div className="space-y-3">
            {/* Aviso si hay pagos realizados */}
            {(budget as any).totalPaid > 0 && (
              <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <CreditCard className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">Este presupuesto tiene pagos realizados</p>
                  <p className="text-sm text-blue-700 mt-0.5">
                    El paciente ha realizado pagos por <strong>{formatCurrency((budget as any).totalPaid)}</strong>.
                    Este importe se trasladará automáticamente a la nueva versión del presupuesto.
                    La deuda se recalculará según el nuevo total.
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <RefreshCw className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                Se creará un nuevo borrador basado en este presupuesto.
                Tendrá el mismo número con versión incrementada.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motivo de la rectificación (opcional)
              </label>
              <textarea
                value={rectifyReason}
                onChange={(e) => setRectifyReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none"
                placeholder="Ej: Cambio de tratamiento solicitado por el paciente..."
              />
            </div>
          </div>
          <ModalFooter
            onCancel={() => setShowRectifyModal(false)}
            onConfirm={handleRectify}
            isLoading={isActing}
            confirmLabel="Crear rectificación"
            confirmClass="bg-amber-600 hover:bg-amber-700 text-white"
          />
        </Modal>
      )}

      {/* Modal programar recordatorio */}
      {showReminderModal && budget && (
        <ReminderModal
          isOpen={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          onSaved={() => loadBudgetReminders()}
          patientId={budget.patient.id}
          patientName={budget.patient.fullName}
          budgetId={budget.id}
          budgetNumber={budget.budgetNumber}
          treatmentName={budget.treatment?.name ?? undefined}
          pendingAmount={(budget as any).pendingAmount ?? null}
        />
      )}

      {/* Modal reenviar email */}
      {showResendModal && (
        <Modal title="Reenviar presupuesto" onClose={() => !isSendingResend && setShowResendModal(false)}>
          <p className="text-sm text-gray-500 mb-4">Confirma o modifica el email de destino.</p>
          <input type="email" value={resendEmail}
            onChange={e => setResendEmail(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !isSendingResend) handleResendEmail(); }}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#008DD0] mb-4"
            placeholder="email@ejemplo.com" autoFocus />
          <ModalFooter
            onCancel={() => setShowResendModal(false)}
            onConfirm={handleResendEmail}
            isLoading={isSendingResend}
            confirmLabel="Enviar"
            confirmClass="bg-[#008DD0] hover:bg-[#007ab8] text-white"
          />
        </Modal>
      )}

    </div>
  );
}

// ── Componentes auxiliares inline ─────────────────────────────────────────────

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({
  onCancel, onConfirm, isLoading, confirmLabel, confirmClass,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  confirmLabel: string;
  confirmClass: string;
}) {
  return (
    <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
      <button
        onClick={onCancel}
        disabled={isLoading}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-colors"
      >
        Cancelar
      </button>
      <button
        onClick={onConfirm}
        disabled={isLoading}
        className={`px-4 py-2 text-sm font-medium rounded-xl disabled:opacity-50 transition-colors flex items-center gap-2 ${confirmClass}`}
      >
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {confirmLabel}
      </button>
    </div>
  );
}

// =============================================================================
// HISTORIAL DE AUDITORÍA — helpers de presentación (timeline UX)
// =============================================================================

interface AuditMessageResult {
  title: string;
  subtitle: string | null;
  dotBg: string;
  iconColor: string;
}

function getAuditMessage(log: any): AuditMessageResult {
  const d = log.details || {};
  switch (log.action) {
    case 'CREADO':
      if (d.isRectification) {
        return {
          title: 'Nueva versión creada',
          subtitle: d.reason ? 'Motivo: ' + d.reason : 'Basada en la versión anterior',
          dotBg: 'bg-amber-50',
          iconColor: 'text-amber-500',
        };
      }
      return { title: 'Presupuesto creado', subtitle: null, dotBg: 'bg-gray-100', iconColor: 'text-gray-400' };

    case 'ACTUALIZADO': {
      const n = Number(d.linesModified) || 0;
      const hasActDetail = d.acts?.length > 0;
      return {
        title: 'Presupuesto modificado',
        subtitle: hasActDetail
          ? null
          : n > 0
            ? 'Se actualizaron ' + n + ' acto' + (n === 1 ? '' : 's') + ' médico' + (n === 1 ? '' : 's')
            : 'Se guardaron los cambios',
        dotBg: 'bg-blue-50',
        iconColor: 'text-blue-400',
      };
    }

    case 'ENVIADO':
      if (d.sendEmail && !d.printOnly) {
        return {
          title: 'Enviado al paciente por email',
          subtitle: 'Se adjuntó el PDF al correo del paciente',
          dotBg: 'bg-[#008DD0]/10',
          iconColor: 'text-[#008DD0]',
        };
      }
      return {
        title: 'Marcado como enviado',
        subtitle: 'El estado cambió a Enviado sin envío de email',
        dotBg: 'bg-[#008DD0]/10',
        iconColor: 'text-[#008DD0]',
      };

    case 'ACEPTADO':
      return {
        title: 'Presupuesto aceptado',
        subtitle: d.notes ? 'Nota: ' + d.notes : 'El paciente aceptó el presupuesto',
        dotBg: 'bg-green-50',
        iconColor: 'text-green-500',
      };

    case 'RECHAZADO':
      return {
        title: 'Presupuesto rechazado',
        subtitle: d.rejectionNotes || 'El paciente rechazó el presupuesto',
        dotBg: 'bg-red-50',
        iconColor: 'text-red-500',
      };

    case 'RECTIFICADO':
      return {
        title: 'Se generó una nueva versión',
        subtitle: d.reason ? 'Motivo: ' + d.reason : 'Se inició una rectificación del presupuesto',
        dotBg: 'bg-amber-50',
        iconColor: 'text-amber-500',
      };

    default:
      return { title: log.action, subtitle: null, dotBg: 'bg-gray-100', iconColor: 'text-gray-400' };
  }
}

function AuditTimelineIcon({ action, colorClass }: { action: string; colorClass: string }) {
  const cls = 'h-3.5 w-3.5 ' + colorClass;
  switch (action) {
    case 'CREADO':      return <FileText className={cls} />;
    case 'ACTUALIZADO': return <RefreshCw className={cls} />;
    case 'ENVIADO':     return <Send className={cls} />;
    case 'ACEPTADO':    return <Check className={cls} />;
    case 'RECHAZADO':   return <X className={cls} />;
    case 'RECTIFICADO': return <RefreshCw className={cls} />;
    default:            return <Clock className={cls} />;
  }
}
